import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import LoginScreen_DBPRO from "@/components/LoginScreen_DBPRO";
import TrainingDashboard_DBPRO from "@/components/TrainingDashboard_DBPRO";
import DashboardScreen_DBPRO_GPS_v2_BrandStyling from "@/components/DashboardScreen_DBPRO_GPS_v2_BrandStyling";
import SessionSummaryPage_DBPRO from "@/components/SessionSummaryPage_DBPRO";
import HistoryScreen_DBPRO from "@/components/HistoryScreen_DBPRO";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import PrivacyPolicyPage from "@/components/PrivacyPolicyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/**
 * AuthCallback - Handles the OAuth redirect with session_id in URL fragment
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL hash
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        console.error('No session_id found in URL');
        navigate('/', { replace: true });
        return;
      }

      try {
        // Exchange session_id for user data
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          throw new Error('Failed to authenticate');
        }

        const data = await response.json();
        
        // Store session token in localStorage as backup (for browsers that block cookies)
        if (data.session_token) {
          localStorage.setItem('dbpro_session_token', data.session_token);
        }
        
        // Store user data in localStorage for persistence
        localStorage.setItem('dbpro_user', JSON.stringify(data));
        
        // Clear the hash and navigate to dashboard with user data
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true, state: { user: data } });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  // Show nothing while processing (silent)
  return null;
};

/**
 * ProtectedRoute - Wraps routes that require authentication
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    // If user passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      // Get session token from localStorage as backup
      const storedToken = localStorage.getItem('dbpro_session_token');
      const storedUser = localStorage.getItem('dbpro_user');
      
      try {
        // Build headers - include token from localStorage if available
        const headers = {};
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        
        const response = await fetch(`${API}/auth/me`, {
          headers
        });

        if (!response.ok) {
          throw new Error('Not authenticated');
        }

        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        
        // Update localStorage with fresh user data
        localStorage.setItem('dbpro_user', JSON.stringify(userData));
      } catch (error) {
        // Clear stored data on auth failure
        localStorage.removeItem('dbpro_session_token');
        localStorage.removeItem('dbpro_user');
        setIsAuthenticated(false);
        navigate('/', { replace: true });
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A1A2F' }}>
        <div className="animate-pulse text-white text-lg">Loading...</div>
      </div>
    );
  }

  // If not authenticated, don't render children (navigate happens in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  // Render children with user context
  return children;
};

/**
 * AppRouter - Main routing with session_id detection
 */
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment synchronously during render (not in useEffect)
  // This prevents race conditions - process session_id BEFORE checking existing session
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Login Screen - First in sequence */}
      <Route path="/" element={<LoginScreen_DBPRO />} />
      
      {/* Reset Password - Public route */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Privacy Policy - Public route */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <TrainingDashboard_DBPRO />
        </ProtectedRoute>
      } />
      
      <Route path="/live-session" element={
        <ProtectedRoute>
          <DashboardScreen_DBPRO_GPS_v2_BrandStyling />
        </ProtectedRoute>
      } />
      
      <Route path="/summary" element={
        <ProtectedRoute>
          <SessionSummaryPage_DBPRO />
        </ProtectedRoute>
      } />
      
      <Route path="/history" element={
        <ProtectedRoute>
          <HistoryScreen_DBPRO />
        </ProtectedRoute>
      } />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
